import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';

import MicTranscribeButton from '../components/MicTranscribeButton';
import ConversationStorageControls from '../components/ConversationStorageControls';
import { FASTAPI_HOST } from '../settings/config';
import { DEFAULT_BUDDY, loadBuddies } from '../settings/buddyProfiles';
import { getBuddyProfiles } from '../settings/buddySessionCache';
import { BuddyWorkspace, ConversationScaffoldAids, ConversationTimeline, EndConversationControl, WorkspaceComposerArea, WorkspaceHeader } from '../components/buddy-workspace';


const COPY = {
  English: {
    title: 'Talk with Leafy',
    subtitle: 'Take your time. A word, a choice, or a sentence is welcome.',
    placeholder: 'Say or type something…',
    send: 'Send',
    end: 'End chat',
    newChat: 'New chat',
    start: 'Let’s start!',
    starting: 'Starting…',
    startAria: 'Start chatting',
    checkingPlan: 'Checking your plan…',
    paidRequired: 'A paid plan is required to start this activity.',
    voiceOn: 'Turn off Buddy voice',
    voiceOff: 'Turn on Buddy voice',
    startError: 'The chat could not start. Please try again.',
    sendError: 'Leafy could not respond. Please try again.',
    voiceError: 'Leafy’s voice is unavailable right now.',
    privacy: 'This is a temporary friendly conversation, not speech therapy or an assessment.',
    settings: 'Creation Settings',
    optionsOn: 'Support ON',
    optionsOff: 'Support OFF',
    drawConversation: 'Draw our conversation',
    storyConversation: 'Make a story',
    creating: 'Creating…',
    creationError: 'Could not create that right now. Please try again.',
  },
  'Simplified Chinese': {
    title: '和 Leafy 聊聊天',
    subtitle: '慢慢说。一个词、一个选择或一句话都可以。',
    placeholder: '说一说或输入文字……',
    send: '发送',
    end: '结束聊天',
    newChat: '新的聊天',
    start: '我们开始吧！',
    starting: '正在开始…',
    startAria: '开始聊天',
    checkingPlan: '正在确认您的方案…',
    paidRequired: '需要付费方案才能开始此活动。',
    voiceOn: '关闭伙伴语音',
    voiceOff: '打开伙伴语音',
    startError: '无法开始聊天，请重试。',
    sendError: 'Leafy 暂时无法回答，请重试。',
    voiceError: 'Leafy 的语音暂时无法播放。',
    privacy: '这是临时的友好聊天，不是语言治疗或能力评估。',
    settings: '创作设置',
    optionsOn: '支持开启',
    optionsOff: '支持关闭',
    drawConversation: '画出我们的对话',
    storyConversation: '创作故事',
    creating: '正在创作…',
    creationError: '暂时无法创作，请重试。',
  },
  'Traditional Chinese': {
    title: '和 Leafy 聊聊天',
    subtitle: '慢慢說。一個詞、一個選擇或一句話都可以。',
    placeholder: '說一說或輸入文字……',
    send: '傳送',
    end: '結束聊天',
    newChat: '新的聊天',
    start: '我們開始吧！',
    starting: '正在開始…',
    startAria: '開始聊天',
    checkingPlan: '正在確認您的方案…',
    paidRequired: '需要付費方案才能開始此活動。',
    voiceOn: '關閉夥伴語音',
    voiceOff: '開啟夥伴語音',
    startError: '無法開始聊天，請重試。',
    sendError: 'Leafy 暫時無法回答，請重試。',
    voiceError: 'Leafy 的語音暫時無法播放。',
    privacy: '這是暫時的友好聊天，不是語言治療或能力評估。',
    settings: '創作設定',
    optionsOn: '支援開啟',
    optionsOff: '支援關閉',
    drawConversation: '畫出我們的對話',
    storyConversation: '創作故事',
    creating: '正在創作…',
    creationError: '暫時無法創作，請重試。',
  },
  French: {
    title: 'Parler avec Leafy',
    subtitle: 'Prends ton temps. Un mot, un choix ou une phrase suffit.',
    placeholder: 'Dis ou écris quelque chose…',
    send: 'Envoyer',
    end: 'Terminer',
    newChat: 'Nouvelle discussion',
    start: 'Commençons !',
    starting: 'Démarrage…',
    startAria: 'Commencer à discuter',
    checkingPlan: 'Vérification de votre forfait…',
    paidRequired: 'Un forfait payant est requis pour démarrer cette activité.',
    voiceOn: 'Désactiver la voix du compagnon',
    voiceOff: 'Activer la voix du compagnon',
    startError: 'La discussion n’a pas pu commencer. Réessaie.',
    sendError: 'Leafy ne peut pas répondre maintenant. Réessaie.',
    voiceError: 'La voix de Leafy est indisponible pour le moment.',
    privacy: 'Cette conversation amicale est temporaire. Ce n’est ni une thérapie du langage ni une évaluation.',
    settings: 'Paramètres de création',
    optionsOn: 'Soutien ACTIVÉ',
    optionsOff: 'Soutien DÉSACTIVÉ',
    drawConversation: 'Dessiner notre conversation',
    storyConversation: 'Créer une histoire',
    creating: 'Création…',
    creationError: 'Création impossible pour le moment. Réessaie.',
  },
};

const languageCopy = (language) => {
  const aliases = {
    en: 'English', fr: 'French', 'zh-CN': 'Simplified Chinese',
    zh: 'Simplified Chinese', 'zh-TW': 'Traditional Chinese',
  };
  return COPY[aliases[language] || language] || COPY.English;
};

const buddyCopy = (copy, name) => Object.fromEntries(
  Object.entries(copy).map(([key, value]) => [key, typeof value === 'string' ? value.replaceAll('Leafy', name) : value]),
);

const errorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return (typeof detail === 'string' ? detail : detail?.message)
    || error?.message || fallback;
};

const creationIntent = (value) => {
  const input = String(value || '').trim().toLowerCase();
  if (!input) return '';
  if (/\b(draw|paint|illustrate|make (?:a |an )?picture)\b|画|畫|dessin/.test(input)) return 'image';
  if (/\b(make|create|write|turn).{0,20}\bstory\b|故事|histoire/.test(input)) return 'story';
  return '';
};

function TimelineArtifact({ block }) {
  const [expanded, setExpanded] = useState(false);
  const isImage = block.type === 'image';
  const label = block.content?.title || block.content?.alt || (isImage ? 'Generated image' : 'Generated story');
  return (
    <ButtonBase
      onClick={() => setExpanded((value) => !value)}
      aria-label={`${expanded ? 'Collapse' : 'Expand'} ${label}`}
      aria-expanded={expanded}
      sx={{ display: 'block', width: expanded ? 'min(68vw, 560px)' : 72, maxWidth: '100%', borderRadius: 2, overflow: 'hidden', textAlign: 'left' }}
    >
      {isImage ? (
        <Box component="img" src={block.content?.url} alt={block.content?.alt || ''} sx={{ display: 'block', width: expanded ? '100%' : 72, height: expanded ? 'auto' : 72, maxHeight: expanded ? '65vh' : 72, objectFit: expanded ? 'contain' : 'cover', bgcolor: '#EDF1DF' }} />
      ) : (
        <Stack spacing={expanded ? 1 : 0} sx={{ width: '100%', p: expanded ? 2 : 1, alignItems: expanded ? 'stretch' : 'center' }}>
          <AutoStoriesRoundedIcon sx={{ fontSize: expanded ? 30 : 42, color: '#3A6752', alignSelf: expanded ? 'flex-start' : 'center' }} />
          {expanded && <><Typography sx={{ fontWeight: 800 }}>{label}</Typography><Typography sx={{ whiteSpace: 'pre-wrap' }}>{block.content?.summary}</Typography></>}
        </Stack>
      )}
    </ButtonBase>
  );
}

export default function FreeTalkPage({
  currentLang = 'English',
  childName = '',
  childAge = 8,
  childInterest = '',
  roleLanguage = '',
  conversationLevel = 'intermediate',
  paidAccess = false,
  planChecked = false,
  onPurchase,
}) {
  const [buddies, setBuddies] = useState(loadBuddies);
  const [buddyId, setBuddyId] = useState(() => window.localStorage.getItem('freeTalkBuddyId') || 'leafy');
  const buddy = buddies.find((item) => item.id === buddyId) || DEFAULT_BUDDY;
  const text = buddyCopy(languageCopy(currentLang), buddy.name);
  const chatEndRef = useRef(null);
  const audioRef = useRef(null);
  const audioUrlRef = useRef('');
  const ttsAbortRef = useRef(null);
  const conversationStartedRef = useRef(false);
  const [sessionId, setSessionId] = useState('');
  const [version, setVersion] = useState(0);
  const [messages, setMessages] = useState([]);
  const [choices, setChoices] = useState([]);
  const [hint, setHint] = useState('');
  const [status, setStatus] = useState('collecting');
  const [answer, setAnswer] = useState('');
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showScaffoldAids, setShowScaffoldAids] = useState(true);
  const [error, setError] = useState('');
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [voicePlaying, setVoicePlaying] = useState(false);
  const [leafyVoice, setLeafyVoice] = useState(buddy.voice || 'ai_01');
  const [speechSpeed, setSpeechSpeed] = useState(1);
  const [creatingArtifact, setCreatingArtifact] = useState('');
  const [artifactBlocks, setArtifactBlocks] = useState([]);
  const [storageMode, setStorageMode] = useState('temporary');

  const authConfig = () => ({
    headers: { Authorization: `Bearer ${Cookies.get('user_token') || ''}` },
  });

  const latestAssistantText = (items = messages) => {
    for (let index = items.length - 1; index >= 0; index -= 1) {
      if (items[index]?.role === 'assistant') return items[index].content || '';
    }
    return '';
  };

  const applyView = (view) => {
    if (view?.context?.persistence_warning) setError(view.context.persistence_warning);
    setSessionId(view?.session_id || '');
    setStorageMode(view?.context?.storage_mode || 'temporary');
    setVersion(Number(view?.version) || 0);
    setMessages(Array.isArray(view?.messages) ? view.messages : []);
    setChoices(Array.isArray(view?.input?.choices) ? view.input.choices : []);
    setHint(String(view?.input?.hint || ''));
    setStatus(view?.status || 'collecting');
  };

  const stopSpeaking = () => {
    if (ttsAbortRef.current) ttsAbortRef.current.abort();
    ttsAbortRef.current = null;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    audioRef.current = null;
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    audioUrlRef.current = '';
    setVoicePlaying(false);
  };

  const speakText = async (value) => {
    const content = String(value || '').trim();
    if (!content) return;
    stopSpeaking();
    setVoicePlaying(true);
    const controller = new AbortController();
    ttsAbortRef.current = controller;
    try {
      const response = await axios.post(
        `${FASTAPI_HOST}/api/photo-story/tts`,
        { text: content, voice: leafyVoice, language: currentLang || 'English' },
        { ...authConfig(), responseType: 'arraybuffer', signal: controller.signal },
      );
      const url = URL.createObjectURL(new Blob([response.data], { type: 'audio/mpeg' }));
      const audio = new Audio(url);
      audio.playbackRate = Number(speechSpeed) || 1;
      audio.preservesPitch = true;
      audioRef.current = audio;
      audioUrlRef.current = url;
      const release = () => {
        if (audioRef.current === audio) audioRef.current = null;
        if (audioUrlRef.current === url) {
          URL.revokeObjectURL(url);
          audioUrlRef.current = '';
        }
        setVoicePlaying(false);
      };
      audio.onended = release;
      audio.onerror = release;
      await audio.play();
    } catch (requestError) {
      if (requestError?.code !== 'ERR_CANCELED') setError(text.voiceError);
      stopSpeaking();
    }
  };

  const startConversation = async (activeBuddy = buddy) => {
    if (starting) return;
    conversationStartedRef.current = true;
    stopSpeaking();
    setStarting(true);
    setError('');
    setAnswer('');
    setArtifactBlocks([]);
    try {
      const normalizedLevel = String(conversationLevel || 'intermediate').toLowerCase();
      const response = await axios.post(
        `${FASTAPI_HOST}/api/conversations/text`,
        {
          definition: 'free_talk',
          child_name: childName || '',
          child_age: Number(childAge) || 8,
          child_interest: childInterest || '',
          role_language: roleLanguage || '',
          language: currentLang || 'English',
          conversation_level: ['basic', 'intermediate', 'advanced'].includes(normalizedLevel)
            ? normalizedLevel : 'intermediate',
          buddy_name: activeBuddy.name,
          buddy_personality: activeBuddy.personality,
        },
        authConfig(),
      );
      applyView(response.data);
    } catch (requestError) {
      setError(errorMessage(requestError, text.startError));
    } finally {
      setStarting(false);
    }
  };

  const sendAction = async (type, value = '') => {
    if (!sessionId || submitting) return;
    stopSpeaking();
    setSubmitting(true);
    setError('');
    try {
      const response = await axios.post(
        `${FASTAPI_HOST}/api/conversations/${sessionId}/actions`,
        {
          request_id: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
          expected_version: version,
          type,
          value,
          conversation_language: currentLang,
        },
        authConfig(),
      );
      applyView(response.data);
      setAnswer('');
      if (voiceEnabled) await speakText(latestAssistantText(response.data?.messages || []));
      const requestedArtifact = type === 'submit_text' ? creationIntent(value) : '';
      if (requestedArtifact) {
        const responseMessages = response.data?.messages || [];
        await generateArtifact(requestedArtifact, true, responseMessages.at(-1)?.id);
      }
    } catch (requestError) {
      setError(errorMessage(requestError, text.sendError));
    } finally {
      setSubmitting(false);
    }
  };

  const generateArtifact = async (type, allowCurrentTurn = false, anchorMessageId = '') => {
    if (!sessionId || creatingArtifact || (!allowCurrentTurn && displayedMessages.every((message) => message.role !== 'user'))) return;
    setCreatingArtifact(type);
    setError('');
    try {
      const endpoint = `${FASTAPI_HOST}/api/conversations/${sessionId}/artifacts/${type}`;
      const payload = type === 'image'
        ? { style: 'children_book', size: '1024x1024' }
        : { pages: 6, writing_language: currentLang, cognitive_level: 'intermediate', story_style: 'warm_expressive', caregiver_notes: '' };
      const { data } = await axios.post(endpoint, payload, authConfig());
      const block = type === 'image'
        ? {
          id: `image-${Date.now()}`,
          type: 'image',
          content: {
            url: `data:${data?.mime_type || 'image/png'};base64,${data?.image_base64 || ''}`,
            alt: text.drawConversation,
          },
        }
        : {
          id: `story-${Date.now()}`,
          type: 'story',
          content: {
            title: data?.title || data?.story_title || text.storyConversation,
            summary: Array.isArray(data?.pages)
              ? data.pages.map((page) => page?.text || page?.content || '').filter(Boolean).join(' ')
              : data?.story || data?.content || data?.summary || '',
          },
        };
      setArtifactBlocks((current) => [...current, {
        ...block,
        anchorMessageId: anchorMessageId || displayedMessages.at(-1)?.id || '',
      }]);
    } catch (requestError) {
      setError(errorMessage(requestError, text.creationError));
    } finally {
      setCreatingArtifact('');
    }
  };

  useEffect(() => {
    Promise.all([
      getBuddyProfiles(),
      axios.get(`${FASTAPI_HOST}/api/user-preferences`, authConfig()),
    ])
      .then(([loaded, preferencesResponse]) => {
        const preferredId = window.localStorage.getItem('freeTalkBuddyId');
        const selected = loaded.find((item) => item.id === preferredId) || loaded.find((item) => item.isDefault) || loaded[0] || buddy;
        setBuddies(loaded.length ? loaded : [selected]);
        setBuddyId(selected.id); setLeafyVoice(selected.voice);
        setSpeechSpeed(Number(preferencesResponse.data?.photo_leafy_speech_speed) || 1);
      })
      .catch(() => {});
  }, []);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, artifactBlocks, submitting]);
  useEffect(() => () => stopSpeaking(), []);
  useEffect(() => {
    const handleBuddyUpdate = (event) => {
      const selected = event.detail;
      if (!selected?.id) return;
      setBuddies((current) => {
        const exists = current.some((item) => item.id === selected.id);
        return exists ? current.map((item) => item.id === selected.id ? selected : item) : [...current, selected];
      });
      setBuddyId(selected.id); setLeafyVoice(selected.voice); setSpeechSpeed(selected.speed);
      if (conversationStartedRef.current) startConversation(selected);
    };
    window.addEventListener('buddy-settings-updated', handleBuddyUpdate);
    return () => window.removeEventListener('buddy-settings-updated', handleBuddyUpdate);
  }, []);

  const displayedMessages = useMemo(
    () => messages.filter((message) => message?.role !== 'system'),
    [messages],
  );
  const timelineMessages = useMemo(() => {
    const pending = [...artifactBlocks];
    const combined = [];
    displayedMessages.forEach((message) => {
      combined.push(message);
      pending.filter((block) => block.anchorMessageId === message.id).forEach((block) => {
        combined.push({ id: block.id, role: 'assistant', content: '', kind: 'artifact', artifact: block });
      });
    });
    pending.filter((block) => !combined.some((message) => message.id === block.id)).forEach((block) => {
      combined.push({ id: block.id, role: 'assistant', content: '', kind: 'artifact', artifact: block });
    });
    return combined;
  }, [displayedMessages, artifactBlocks]);
  const completed = status === 'completed';

  return (
    <BuddyWorkspace activity="free_talk" maxWidth={920} sx={{ px: 2, pt: 9, pb: { xs: 2, md: 4 } }}>
      <Box sx={{ border: '1px solid #D6DED2', borderRadius: 3, overflow: 'hidden', bgcolor: '#fff' }}>
        <WorkspaceHeader avatar={buddy.image} buddyId={buddy.id} avatarFallback={buddy.reactions?.neutral || '🙂'} title={text.title} badge="Beta" actions={<>
            <ConversationStorageControls sessionId={sessionId} storageMode={storageMode} definitions={['free_talk']} onRestore={applyView} onStorageModeChange={setStorageMode} currentLang={currentLang} />
          </>} />

        {!sessionId ? (
          <Box sx={{ minHeight: 360, display: 'grid', placeItems: 'center', px: 2, py: 5, textAlign: 'center', bgcolor: '#FDFCF8' }}>
            <Stack spacing={2} alignItems="center">
              {error && <Alert severity="error">{error}</Alert>}
              <Typography sx={{ color: 'text.secondary', maxWidth: 520 }}>{text.subtitle}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 560 }}>
                {text.privacy}
              </Typography>
              <Button
                variant="contained"
                onClick={() => (paidAccess ? startConversation(buddy) : onPurchase?.())}
                aria-label={text.startAria}
                disabled={!planChecked || starting}
                startIcon={starting ? <CircularProgress size={18} color="inherit" /> : null}
                sx={{ bgcolor: '#274D3B', borderRadius: 999, px: 4, py: 1.25, fontWeight: 800, '&:hover': { bgcolor: '#1F3D30' } }}
              >
                {starting ? text.starting : text.start}
              </Button>
              {!planChecked ? (
                <Typography variant="caption" color="text.secondary">{text.checkingPlan}</Typography>
              ) : !paidAccess ? (
                <Typography variant="caption" color="text.secondary">{text.paidRequired}</Typography>
              ) : null}
            </Stack>
          </Box>
        ) : (<>
          <ConversationTimeline messages={timelineMessages} busy={submitting} endRef={chatEndRef} contentRenderer={(message) => message.artifact ? <TimelineArtifact block={message.artifact} /> : null} reactionRenderer={(message) => <Typography component="span" role="img" aria-label={`${buddy.name} reaction: ${message.reaction || 'neutral'}`} sx={{ fontSize: 18, lineHeight: 1 }}>{buddy.reactions?.[message.reaction] || buddy.reactions?.neutral || '🙂'}</Typography>} />

          <WorkspaceComposerArea>
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          {!completed && (
            <ConversationScaffoldAids
              choices={choices}
              hint={hint}
              visible={showScaffoldAids}
              disabled={submitting}
              language={currentLang}
              onSelect={(value) => sendAction('select_choice', value)}
              onSpeak={speakText}
              onSkip={(label) => sendAction('skip_question', label)}
              trailingActions={<>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={showScaffoldAids}
                      onChange={(event) => setShowScaffoldAids(event.target.checked)}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#3A6752' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#3A6752' },
                      }}
                    />
                  )}
                  label={showScaffoldAids ? text.optionsOn : text.optionsOff}
                  sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 14, fontWeight: 700 } }}
                />
                <Tooltip title={voiceEnabled ? text.voiceOn : text.voiceOff}>
                  <IconButton
                    size="small"
                    disabled={!sessionId}
                    onClick={async () => {
                      if (voiceEnabled) { setVoiceEnabled(false); stopSpeaking(); }
                      else { setVoiceEnabled(true); await speakText(latestAssistantText()); }
                    }}
                    sx={{ border: '1px solid #d1d5db', color: voiceEnabled ? '#2f7d32' : '#6b7280' }}
                  >
                    {voicePlaying ? <CircularProgress size={20} /> : voiceEnabled ? <VolumeUpRoundedIcon /> : <VolumeOffRoundedIcon />}
                  </IconButton>
                </Tooltip>
              </>}
            />
          )}
          {!completed ? (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                <TextField
                  fullWidth autoComplete="off" value={answer} disabled={submitting}
                  onChange={(event) => setAnswer(event.target.value)}
                  onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); if (answer.trim()) sendAction('submit_text', answer.trim()); } }}
                  placeholder={text.placeholder}
                  InputProps={{ endAdornment: <InputAdornment position="end"><MicTranscribeButton insideInput currentLang={currentLang} followUiLanguage disabled={submitting} audioOutputActive={voicePlaying} onTranscript={(value) => setAnswer(String(value || '').trim())} onError={setError} onRecordingStart={stopSpeaking} /></InputAdornment> }}
                />
                <Button variant="contained" endIcon={<SendRoundedIcon />} disabled={!answer.trim() || submitting} onClick={() => sendAction('submit_text', answer.trim())} sx={{ bgcolor: '#274D3B', minWidth: 110, '&:hover': { bgcolor: '#1F3D30' } }}>{text.send}</Button>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.25 }}>
                <Button size="small" startIcon={creatingArtifact === 'image' ? <CircularProgress size={16} /> : <AutoAwesomeRoundedIcon />} disabled={Boolean(creatingArtifact) || displayedMessages.every((message) => message.role !== 'user')} onClick={() => generateArtifact('image')}>{creatingArtifact === 'image' ? text.creating : text.drawConversation}</Button>
                <Button size="small" startIcon={creatingArtifact === 'story' ? <CircularProgress size={16} /> : <AutoStoriesRoundedIcon />} disabled={Boolean(creatingArtifact) || displayedMessages.every((message) => message.role !== 'user')} onClick={() => generateArtifact('story')}>{creatingArtifact === 'story' ? text.creating : text.storyConversation}</Button>
              </Stack>
              <EndConversationControl label={text.end} disabled={submitting} onEnd={(label) => sendAction('end_conversation', label)} sx={{ mt: 1.5 }} />
            </>
          ) : (
            <Button variant="contained" startIcon={starting ? <CircularProgress size={18} color="inherit" /> : <ReplayRoundedIcon />} disabled={starting} onClick={startConversation} sx={{ bgcolor: '#274D3B', '&:hover': { bgcolor: '#1F3D30' } }}>{starting ? text.starting : text.newChat}</Button>
          )}
          </WorkspaceComposerArea>
        </>)}
      </Box>
    </BuddyWorkspace>
  );
}
